import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Input, Button } from '../components';
import { useAuth } from '../hooks';
import { ICON_STROKE_WIDTH } from '../constants';
import { authService } from '../auth/authService';
import { resolvePostAuthPathname } from '../utils/getDefaultRoute';
import '../theme/index.css';
import './Login.css';

interface LoginFormData {
  username: string;
  password: string;
}

interface LocationState {
  from?: {
    pathname: string;
  };
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuth();

  useEffect(() => {
    if (authService.isRisAuthenticated()) {
      const stored = (location.state as LocationState)?.from?.pathname;
      const target = resolvePostAuthPathname(stored, authService.getUser());
      navigate(target, { replace: true });
    }
  }, [navigate, location]);

  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: '',
  });

  const [formErrors, setFormErrors] = useState<Partial<LoginFormData>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof LoginFormData]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
    if (error) {
      clearError();
    }
  };

  const validateForm = (): boolean => {
    const errors: Partial<LoginFormData> = {};
    if (!formData.username.trim()) {
      errors.username = 'Email / Username is required';
    }
    if (!formData.password) {
      errors.password = 'Password is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const response = await login({
      username: formData.username,
      password: formData.password,
    });

    if (response.success) {
      const stored = (location.state as LocationState)?.from?.pathname;
      const target = resolvePostAuthPathname(stored, authService.getUser());
      navigate(target, { replace: true });
    }
  };

  return (
    <div className="login-page-light">
      <div className="login-inner">
        <div className="login-logo-wrap">
          <div className="login-logo-icon">
            <Sparkles
              strokeWidth={ICON_STROKE_WIDTH}
              className="login-logo-icon-svg"
            />
          </div>
          <span className="login-logo-text">RadiologyIQ</span>
        </div>

        <div className="login-card-light">
          <div className="login-card-header">
            <h1 className="login-card-title">Login to your account</h1>
            <p className="login-card-desc">
              Enter your email or username below to login to your account
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="login-card-content">
              <div className="form-group">
                <label
                  className="form-label"
                  htmlFor="login-email"
                >
                  Email or Username
                </label>
                <Input
                  id="login-email"
                  label=""
                  type="text"
                  name="username"
                  placeholder="Enter Email or Username"
                  value={formData.username}
                  onChange={handleInputChange}
                  error={formErrors.username}
                  disabled={isLoading}
                  autoComplete="username"
                  required
                />
              </div>

              <div className="form-group">
                <label
                  className="form-label"
                  htmlFor="login-password"
                >
                  Password
                </label>
                <Input
                  id="login-password"
                  label=""
                  type="password"
                  name="password"
                  placeholder="Enter Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  error={formErrors.password}
                  showPasswordToggle
                  disabled={isLoading}
                  autoComplete="current-password"
                  required
                />
              </div>

              {error && !formErrors.username && !formErrors.password && (
                <div
                  className="error-message"
                  role="alert"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                className="login-btn-primary-light"
                isLoading={isLoading}
                disabled={isLoading}
              >
                Login
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
